import React, { useEffect, useRef, useState } from 'react';
import service, { type BillToUploadResult, type VendorUploadResult } from '../../services/sage.service';
import { REPORT_BUTTON } from './reports/reportUtils';

/**
 * Upload Sage Bill Tos / Upload Sage Vendors.
 *
 * The Bill To upload has a consequence its name does not suggest: a change to a
 * bill-to's credit terms or credit hold re-flags every undelivered order for that
 * bill-to as COD and emails the CSRs (#22179). V1 does that silently and the user finds
 * out when a driver cannot move. It is shown here.
 */
type Kind = 'billtos' | 'vendors';

const COPY = {
  billtos: {
    title: 'Bill Tos',
    table: 'billtos',
    accept: '.txt,.csv,text/plain,text/csv',
    columns: 'Customer ID, Customer name, Term, Total due, Customer type ID, On hold, City, '
      + 'Address line 1, Address line 2, State/Territory, Zip code/Post code, Credit limit',
  },
  vendors: {
    title: 'Vendors',
    table: 'vendors',
    accept: '.csv,text/csv',
    columns: 'Vendor ID, Vendor Name, Total Due, Vendor Type ID, City, State/Territory, '
      + 'Zip code/Post code, Term, Contact name',
  },
} as const;

function errorsOf(error: unknown): string[] {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object' && Array.isArray((detail as { errors?: string[] }).errors)) {
    return (detail as { errors: string[] }).errors;
  }
  return [typeof detail === 'string' ? detail : 'The upload failed. Please try again.'];
}

const SageUploadCard: React.FC<{ kind: Kind }> = ({ kind }) => {
  const copy = COPY[kind];
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [result, setResult] = useState<BillToUploadResult | VendorUploadResult | null>(null);
  const [lastRun, setLastRun] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    service.getUploadStatus()
      .then((s) => { if (!cancelled) setLastRun(s[kind]); })
      .catch(() => { /* cosmetic — the upload works without knowing when it last ran */ });
    return () => { cancelled = true; };
  }, [kind, result]);

  const run = async () => {
    if (!file) return;
    setBusy(true); setErrors([]); setResult(null);
    try {
      setResult(kind === 'billtos' ? await service.uploadBillTos(file) : await service.uploadVendors(file));
      setFile(null);
      if (input.current) input.current.value = '';
    } catch (e) {
      setErrors(errorsOf(e));
    } finally { setBusy(false); }
  };

  const cod = result && 'codHeld' in result ? result : null;
  return (
    <div className="flex-1 min-h-0 p-4 space-y-4 text-label overflow-auto">
      <p className="text-sm text-muted">
        Takes the {copy.title.toLowerCase()} file exported from Sage and updates
        <code className="mx-1">{copy.table}</code>. Rows that have not changed are left alone.
        {lastRun && <> Last imported <strong>{new Date(lastRun).toLocaleString()}</strong>.</>}
      </p>

      <div className="flex items-center gap-3 flex-wrap">
        <input ref={input} type="file" accept={copy.accept} disabled={busy}
          aria-label={`${copy.title} file`}
          onChange={(e) => { setFile(e.target.files?.[0] ?? null); setErrors([]); setResult(null); }}
          className="text-sm" />
        <button className={REPORT_BUTTON} onClick={run} disabled={busy || !file}>
          {busy ? 'Importing…' : 'Import'}
        </button>
      </div>
      <details className="text-xs text-muted">
        <summary className="cursor-pointer">Expected columns</summary>
        <p className="mt-1 font-mono">{copy.columns}</p>
      </details>

      {errors.length > 0 && (
        <div className="rounded border border-red-400 bg-red-50 dark:bg-red-950/30 p-3 space-y-1">
          <div className="text-sm font-semibold text-red-700 dark:text-red-300">
            Nothing was imported.
          </div>
          {errors.map((e) => <div key={e} className="text-sm text-red-700 dark:text-red-300">{e}</div>)}
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="flex gap-6 text-sm">
            <span><strong>{result.created.length}</strong> created</span>
            <span><strong>{result.updated.length}</strong> updated</span>
            <span><strong>{result.unchanged}</strong> unchanged</span>
            <span className="text-muted">{result.rows} rows read</span>
          </div>
          {cod && (cod.codHeld.length > 0 || cod.codReleased.length > 0) && (
            <div className="rounded border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2">
              <div className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                Credit terms changed, so these orders were re-flagged
              </div>
              {cod.codHeld.length > 0 && (
                <CodList title="COD Hold — Do Not Move" changes={cod.codHeld} />
              )}
              {cod.codReleased.length > 0 && (
                <CodList title="COD Released — Available to Move" changes={cod.codReleased} />
              )}
              {cod.notified.length > 0 && (
                <div className="text-xs text-muted">Notified: {cod.notified.join(', ')}</div>
              )}
            </div>
          )}
          {(result.created.length > 0 || result.updated.length > 0) && (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted">Which Sage codes</summary>
              <div className="mt-1 space-y-1 font-mono">
                {result.created.length > 0 && <div>created: {result.created.join(', ')}</div>}
                {result.updated.length > 0 && <div>updated: {result.updated.join(', ')}</div>}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

const CodList: React.FC<{ title: string; changes: { pronumber: number; billto: string }[] }> = ({ title, changes }) => (
  <div>
    <div className="text-xs font-semibold text-amber-800 dark:text-amber-200">{title}</div>
    <ul className="text-xs font-mono">
      {changes.map((c) => <li key={c.pronumber}>{c.pronumber} — {c.billto}</li>)}
    </ul>
  </div>
);

export default SageUploadCard;
